import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Group, Stack, Alert, Anchor, Skeleton, SegmentedControl, JsonInput, Grid, Title, Text } from '@mantine/core';
import { IconExclamationCircle, IconCode, IconLayout } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useCsrf } from '../../hooks/useCsrf';
import useSWR from 'swr';
import {
    deviceConfigFetcher,
    updateDeviceConfigByDeviceId,
    buildSparseVisualConfigPatch,
    isValidIframeUri,
} from '../../data/DeviceConfig';
import type { DeviceConfig, DeviceConfigResponsePayload } from '../../data/DeviceConfig';
import { deviceFetcher } from '../../data/Device';
import { CLOUD_UI_PLUGIN_ID } from './componentRegistry';
import { AxiosError } from 'axios';
import VisualConfigEditor from './VisualConfigEditor';
import ScreenPreview from './ScreenPreview';

const DEFAULT_CONFIG: DeviceConfig = {
    pluginId: '',
    isDebug: null,
    topPanel: null,
    middlePanel: null,
    bottomPanel: null,
    iframeUri: null,
};

const normalizeDeviceConfig = (raw: Partial<DeviceConfig> | null | undefined): DeviceConfig => ({
    pluginId: raw?.pluginId ?? '',
    isDebug: raw?.isDebug ?? null,
    topPanel: raw?.topPanel ?? null,
    middlePanel: raw?.middlePanel ?? null,
    bottomPanel: raw?.bottomPanel ?? null,
    iframeUri: raw?.iframeUri ?? null,
});

const DeviceConfigForm: React.FC = () => {
    const { csrfToken } = useCsrf();
    const navigate = useNavigate();
    const { deviceId } = useParams();

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [editorMode, setEditorMode] = useState<'visual' | 'json'>('visual');
    const [config, setConfig] = useState<DeviceConfig>(DEFAULT_CONFIG);
    const [jsonString, setJsonString] = useState<string>('{}');
    const [jsonSwitchError, setJsonSwitchError] = useState<string>('');

    const {
        data: deviceConfigData,
        isLoading,
        mutate,
    } = useSWR<DeviceConfigResponsePayload, AxiosError>(
        deviceId ? `api/device/${deviceId}/config` : null,
        deviceConfigFetcher
    );

    const { data: deviceData } = useSWR(deviceId ? `api/device/${deviceId}` : null, deviceFetcher);
    const deviceName = deviceData?.name?.trim() ? deviceData.name : 'Unnamed';
    const deviceIdSuffix = deviceData?.id ? deviceData.id.slice(-4) : '';

    useEffect(() => {
        if (deviceConfigData) {
            const next = normalizeDeviceConfig(deviceConfigData.config);
            setConfig(next);
            setJsonString(JSON.stringify(next, null, 2));
        }
    }, [deviceConfigData]);

    const handleModeChange = (mode: string) => {
        setJsonSwitchError('');
        if (mode === 'json') {
            setJsonString(JSON.stringify(config, null, 2));
            setEditorMode('json');
        } else {
            try {
                const parsed = JSON.parse(jsonString) as DeviceConfig;
                setConfig(normalizeDeviceConfig(parsed));
                setEditorMode('visual');
            } catch {
                setJsonSwitchError('Fix the JSON errors before switching to the visual editor.');
            }
        }
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);
        setErrorMessage('');

        let payload: Record<string, unknown>;
        if (editorMode === 'json') {
            try {
                payload = JSON.parse(jsonString) as Record<string, unknown>;
            } catch {
                setErrorMessage('Invalid JSON — please fix before saving.');
                setIsSubmitting(false);
                return;
            }
        } else {
            if (config.pluginId === CLOUD_UI_PLUGIN_ID && !isValidIframeUri(config.iframeUri ?? '')) {
                setErrorMessage('Cloud UI requires a valid http or https URL before saving.');
                setIsSubmitting(false);
                return;
            }
            payload = buildSparseVisualConfigPatch(config);
        }

        try {
            if (deviceId) {
                /**
                 * The "raw json" form will completely replace the existing device config
                 * but the "visual" editor will perform a sparse update.
                 * This allows the visual editor to only show relevant form fields for the selected plugin without
                 * "nulling" out field values that have been set for other plugins. I can switch from "cloud-ui-v1" back to "draftkings-v1"
                 * and all my topPanel, middlePanel, and bottomPanel values will still be there.
                 */
                await updateDeviceConfigByDeviceId(csrfToken, deviceId, payload, {
                    replace: editorMode === 'json',
                });
            }
            mutate();
            notifications.show({
                title: 'Configuration Updated',
                message: 'Successfully updated device configuration',
            });
        } catch (err: any) {
            try {
                setErrorMessage(`${err?.message}: ${JSON.parse(err?.response?.data).message}`);
            } catch {
                setErrorMessage(err?.message || 'An unexpected error occurred.');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const renderContent = () => (
        <Stack gap="lg">
            <Group>
                <SegmentedControl
                    value={editorMode}
                    onChange={handleModeChange}
                    data={[
                        {
                            value: 'visual',
                            label: (
                                <Group gap={6} wrap="nowrap">
                                    <IconLayout size={14} />
                                    <span>Visual Editor</span>
                                </Group>
                            ),
                        },
                        {
                            value: 'json',
                            label: (
                                <Group gap={6} wrap="nowrap">
                                    <IconCode size={14} />
                                    <span>JSON Editor</span>
                                </Group>
                            ),
                        },
                    ]}
                />
            </Group>

            {jsonSwitchError && (
                <Alert variant="light" color="orange" title="Cannot switch mode">
                    {jsonSwitchError}
                </Alert>
            )}

            {editorMode === 'visual' ? (
                <VisualConfigEditor config={config} onChange={setConfig} />
            ) : (
                <JsonInput
                    label="Configuration JSON"
                    placeholder="{}"
                    description="Save replaces the entire stored configuration with this JSON. Use the visual editor for partial (sparse) updates."
                    formatOnBlur
                    validationError="Invalid JSON"
                    autosize
                    minRows={15}
                    value={jsonString}
                    onChange={setJsonString}
                />
            )}

            <Group justify="flex-end">
                <Button onClick={handleSubmit} loading={isSubmitting}>
                    Save
                </Button>
            </Group>

            {errorMessage && (
                <Alert variant="light" color="red" title="Error" icon={<IconExclamationCircle />}>
                    {errorMessage}
                </Alert>
            )}
        </Stack>
    );

    return (
        <div style={{ paddingTop: '16px' }}>
            <Anchor onClick={() => navigate('/')}>Back</Anchor>
            <Group gap="sm" align="baseline" mt="sm" mb="md">
                <Title order={2}>{deviceName}</Title>
                {deviceIdSuffix && (
                    <Text size="sm" c="dimmed">
                        {deviceIdSuffix}
                    </Text>
                )}
            </Group>
            {deviceId && isLoading ? (
                <Stack align="flex-end">
                    <Skeleton height={32} mt={6} radius="md" />
                    <Skeleton height={32} mt={6} width="100" radius="md" />
                </Stack>
            ) : (
                <Grid gutter="xl" align="flex-start">
                    <Grid.Col span={{ base: 12, lg: 7 }}>{renderContent()}</Grid.Col>
                    <Grid.Col span={{ base: 12, lg: 5 }}>
                        <ScreenPreview config={config} />
                    </Grid.Col>
                </Grid>
            )}
        </div>
    );
};

export default DeviceConfigForm;
